import React, { useState } from 'react';
import { Search, Bot, BarChart, Lightbulb, Target, Users } from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import WorkspaceKeywordTabs from './WorkspaceKeywordTabs';
import { AiAnalysisTabs } from '@/modules/app/ai-analysis/components';
import { useAiAnalysisData } from '@/modules/app/hooks/useAiAnalysisData';
import { useKeywordCategorization } from '@/modules/app/hooks/useKeywordCategorization';

interface WorkspaceKeywordData {
  id: string;
  keyword?: string;
  primary_keyword: string;
  data_type: string;
  search_volume?: number;
  intent?: string;
  competition?: string;
  difficulty?: number;
  cpc?: number;
  funnel_stage?: string;
  created_at: string;
  subtopic?: string;
  people_also_ask?: string;
  answer?: string;
  url?: string;
  title?: string;
  domain?: string;
  description?: string;
  date_from?: string;
  date_to?: string;
}

interface WorkspaceMainTabsProps {
  keywordData: WorkspaceKeywordData[];
  searchFilter: string;
  isLoading?: boolean;
  workspaceName?: string;
  workspaceId: string;
  userId: string;
  // AI Analysis functionality
  onAiAnalysis?: () => void;
  isAiAnalysisLoading?: boolean;
  allKeywordDataCount?: number;
}

const WorkspaceMainTabs: React.FC<WorkspaceMainTabsProps> = ({
  keywordData,
  searchFilter,
  isLoading = false,
  workspaceName,
  workspaceId,
  userId,
  onAiAnalysis,
  isAiAnalysisLoading = false,
  allKeywordDataCount = 0
}) => {
  const [activeMainTab, setActiveMainTab] = useState('keyword-research');

  // Calculate tab counts
  const keywordResearchCount = keywordData.length;
  
  // ✅ ADD AI ANALYSIS COUNT CALCULATION
  const { categorizations } = useKeywordCategorization(workspaceId, userId);
  const { data: aiData, isLoading: aiDataLoading } = useAiAnalysisData({ workspaceId });
  
  // Calculate total AI analysis count from all sources
  const aiAnalysisCount = categorizations.length + 
                         aiData.contentBriefs.length + 
                         aiData.keywordClusters.length +
                         aiData.funnelStages.length;
  
  return (
    <div className="w-full">
      <Tabs value={activeMainTab} onValueChange={setActiveMainTab} className="w-full">
        <div className="border-b border-border/30 mb-6">
          <div className="flex space-x-8">
            <button
              onClick={() => setActiveMainTab('keyword-research')}
              className={`flex items-center space-x-2 px-1 py-3 text-sm font-medium transition-colors border-b-2 ${
                activeMainTab === 'keyword-research'
                  ? 'text-foreground border-foreground'
                  : 'text-muted-foreground border-transparent hover:text-foreground hover:border-border'
              }`}
            >
              <Search className="h-4 w-4" />
              <span>Keyword Research</span>
              {keywordResearchCount > 0 && (
                <span className="ml-1.5 text-xs text-muted-foreground">
                  {keywordResearchCount}
                </span>
              )}
            </button>
            
            <button
              onClick={() => setActiveMainTab('ai-analysis')}
              className={`flex items-center space-x-2 px-1 py-3 text-sm font-medium transition-colors border-b-2 ${
                activeMainTab === 'ai-analysis'
                  ? 'text-foreground border-foreground'
                  : 'text-muted-foreground border-transparent hover:text-foreground hover:border-border'
              }`}
            >
              <Bot className="h-4 w-4" />
              <span>AI Analysis</span>
              {aiAnalysisCount > 0 && !aiDataLoading && (
                <span className="ml-1.5 text-xs text-muted-foreground">
                  {aiAnalysisCount}
                </span>
              )}
              {aiDataLoading && (
                <span className="ml-1.5 text-xs text-muted-foreground">
                  ...
                </span>
              )}
            </button>
          </div>
        </div>

        <TabsContent value="keyword-research" className="mt-6">
          <WorkspaceKeywordTabs
            keywordData={keywordData}
            searchFilter={searchFilter}
            isLoading={isLoading}
            workspaceName={workspaceName}
            onAiAnalysis={onAiAnalysis}
            isAiAnalysisLoading={isAiAnalysisLoading}
            allKeywordDataCount={allKeywordDataCount}
          />
        </TabsContent>

        <TabsContent value="ai-analysis" className="mt-6">
          <AiAnalysisTabs
            workspaceId={workspaceId}
          />
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default WorkspaceMainTabs;